import { isStandalone, notificationPermission } from './pwa.js'

/**
 * The onboarding gate: A1 + A2.
 *
 * SUPERSEDES THE SHOWN-NOT-GRANTED RULE this file used to hold. That rule
 * routed her to any step she had not been PRESENTED, and let her pass by
 * declining. The operator has overridden it: onboarding now blocks until the
 * app is installed AND notification permission is granted, on BOTH platforms,
 * because without that the install step persuades nothing and the study loses
 * the engagement it depends on.
 *
 * So the state is no longer ours to record. Both facts are read LIVE from the
 * platform every time — `display-mode: standalone` and `Notification.permission`
 * — and no marker is stored anywhere. That is deliberate and it is the part of
 * the old design worth deleting rather than adapting: a stored "shown" marker
 * could disagree with the device, and the device is the only thing that
 * actually decides whether a notification can arrive.
 *
 * It also means the gate self-heals. She installs, the media query flips, and
 * the block lifts with no state to invalidate; she revokes permission in
 * settings and the gate closes again, correctly, with nothing to clean up.
 *
 * WHY THESE TWO ARE DIFFERENT FROM CONSENT AND DEMOGRAPHICS. Those are gated on
 * completion because the server holds the answer. These are facts about a
 * BROWSER ON A PHONE: permission is per-origin per-browser and the home-screen
 * icon is per-device, so a participant on a second phone genuinely has neither
 * and correctly meets both gates again. Nothing about that changes here.
 *
 * ⚠️ THE GATE IS ON PERMISSION ONLY. A3 WAS TRIED AND REVERTED (#143).
 * The paragraph this replaces said the opposite, and it was right for a premise
 * that no longer holds, so it is kept rather than deleted:
 *
 *   "THE GATE IS ON PERMISSION, NOT ON SUBSCRIPTION. `permission === 'granted'`
 *   is a fact she controls. A push SUBSCRIPTION failing is our problem — a dead
 *   VAPID key, a service-worker error, a server 500 — and she cannot fix any of
 *   it from her phone. Blocking on subscription would strand her at a gate with
 *   no action available to her at all."
 *
 * That reasoning rested on EMAIL CATCHING HER. Email has been removed as a
 * transport. A participant who is granted but not subscribed is now not
 * "reachable by a worse channel", she is reachable by NOTHING: she answers 178
 * day-0 items, hears nothing for five days, and no check anywhere reports it.
 *
 * The trade is now the other way round. Being stopped at a screen with a retry
 * is visible and recoverable; passing this gate unsubscribed is neither.
 *
 * ⚠️ AND THEN IT WAS REVERTED, because the cost was worse than predicted.
 * The predicted cost was "if push breaks on our side, nobody enrols". The
 * ACTUAL cost was measured: putting this check in Home's render path trapped
 * the BEEP flow - infra/lifecycle_walk.mjs stalled at beep 2 on a Continue that
 * never advanced, twice, and reverting Home alone restored 21 clean beeps.
 *
 * So the wall lives on the /notifications SCREEN and nowhere else, and the leak
 * it cannot close is written down rather than quietly forgotten: a participant
 * who is granted-but-unsubscribed and RELAUNCHES is routed by Home past the
 * wall to /day0. qa/funnel-guard.mjs prints that gap on every green run.
 *
 */

/** Installed to the home screen. A1: `display-mode: standalone`. */
export function isInstalled() {
  return isStandalone()
}

/** Permission actually granted by the browser, never inferred from a marker. */
export function isPermissionGranted() {
  return notificationPermission() === 'granted'
}

/**
 * The first gate still closed, or null when onboarding may proceed.
 *
 * Install is checked first because on iOS the home screen is a PRECONDITION for
 * a notification existing at all: asking for permission on an uninstalled iOS
 * browser spends the one and only prompt on a device that cannot yet use it.
 *
 * DENIED DOES NOT BLOCK. It routes her to the permission screen, which is where
 * the explanation and the re-check live — but it must never hold her there
 * forever, because she cannot undo it from inside the app. The screen owns that
 * escape; this function only decides where she is sent.
 */
export function nextClosedGate() {
  if (!isInstalled()) return '/install'
  if (isPermissionGranted()) return null
  /*
   * NO ESCAPE. Operator's reversal, and the reasoning it replaces is worth
   * keeping because it was wrong for a specific reason.
   *
   * The old rule said a denied permission must have a way past or she is
   * stranded forever. That premise does not hold HERE: A2 guarantees she is
   * always in an installed standalone app, so this is an OS-level APP
   * permission, not a browser site permission, and she can re-enable it in
   * system settings on both platforms. The dead end being guarded against does
   * not exist, so the escape bought nothing and cost the study its engagement.
   *
   * The denied screen therefore has no forward action at all until permission
   * reads `granted` — only the explanation, the per-platform settings path, and
   * Check again.
   */
  return '/notifications'
}

/*
 * The permission-denied "proceed" flag and its two accessors are DELETED, not
 * left unused and not renamed. It recorded a decision the participant can no
 * longer make, and a key that nothing writes is exactly the kind of thing a
 * later reader finds a use for.
 *
 * Naming it here in its old spelling would be the same trap one layer down, so
 * it is described rather than quoted: if a future change needs this sentence to
 * point at a real key, the escape has come back.
 */
